import React, { Component } from 'react'
import { Descriptions, Form, FormInstance, Input, Select, Tag } from 'antd'
import { DEFAULT_SELECT_FILTER_OPTION } from 'helpers/form-fields.helper'
import { ShelterConsumer, ShelterContextData } from 'context/ShelterContext'
import { CountryConsumer, CountryData } from 'context/CountryContext'
import { PartnerKeyType } from 'interfaces/IIntegration'
import { sbListCountry, sbListState } from 'services/integration'
import ShelterBuddyExternalField from '../ShelterBuddyExternalField'
import MappingStepper, { Step } from '../MappingStepper'
import { DIRECTION_LABEL, Flow, Partner } from '../shared/types'
import { resolveFlow } from '../shared/resolvers'
import { makeSbLoader, mapSbOptions, ensureSbOk } from '../shared/sbOptions'
import { ActiveField, IncomingStep, IntegrationField, ManualExternalInputs, PartnerField, ScopeField } from '../shared/fields'

/** Loads the Shelter Buddy country list for an integration. */
const loadCountries = makeSbLoader(sbListCountry, 'countries')

type MappingStateFormProps = {
  form: FormInstance
  partnerList: Partner[] | null
  /** Save-in-progress flag, forwarded to the final-step Save button. */
  loading?: boolean
}

type MappingStateFormInnerProps = MappingStateFormProps & {
  shelterContext: ShelterContextData
  countryContext: CountryData
}

type MappingStateFormInnerState = {
  flow: Flow
}

class MappingStateFormInner extends Component<MappingStateFormInnerProps, MappingStateFormInnerState> {
  constructor(props: MappingStateFormInnerProps) {
    super(props)
    this.state = {
      flow: resolveFlow(props.form, 'state_uuid'),
    }
  }

  componentDidMount() {
    const { shelterContext, countryContext, form } = this.props

    // Initial values may settle after the Form mounts — re-resolve the flow.
    const flow = resolveFlow(form, 'state_uuid')
    if (flow !== this.state.flow) {
      this.setState({ flow })
    }

    if (!shelterContext.fetching && !shelterContext.shelters?.length) {
      shelterContext.requestShelterList({
        options: {
          limit: 9999,
          sort: ['name'],
        },
        search: {
          _fields: ["id", "uuid", "name"],
          active: true,
          deleted: false,
          organization_integrations: {
            with: {
              _fields: ["id", "uuid", "description", "partner_key", "main", "shelter_id"],
              active: true,
              deleted: false,
            }
          }
        }
      })
    }

    if (!countryContext.fetching && !countryContext.countries?.length) {
      countryContext.requestCountryList({
        options: {
          limit: 9999,
          sort: ['name'],
        },
        search: {
          states: { with: true }
        }
      })
    }
  }

  componentDidUpdate() {
    this.ensureCountryFromState()
  }

  /** On edit we only have the saved `state_uuid` — preselect its country so the state list shows up. */
  ensureCountryFromState = () => {
    const { form, countryContext } = this.props
    const countries = countryContext.countries
    if (!countries) return

    const stateUuid = form.getFieldValue('state_uuid')
    const countryUuid = form.getFieldValue('country_uuid')

    if (stateUuid && !countryUuid) {
      const country = countries.find((c: any) => c.states?.some((s: any) => s.uuid === stateUuid))
      if (country) {
        form.setFieldsValue({ country_uuid: country.uuid })
      }
    }
  }

  /** Loads the Shelter Buddy state list for the selected external country. */
  loadStates = async (integrationUuid: string) => {
    const countryId = Number(this.props.form.getFieldValue('external_country_id'))
    const response = await sbListState({ integration_uuid: integrationUuid, country_id: countryId })
    ensureSbOk(response, 'states')
    return mapSbOptions(response.data.data)
  }

  /** Resolve a shelter name for display, from the loaded shelter list. */
  shelterName = (uuid?: string) =>
    this.props.shelterContext.shelters?.find(s => s.uuid === uuid)?.name

  /** Resolve a partner display name. */
  partnerName = (key?: string) =>
    this.props.partnerList?.find(p => p.key === key)?.name || key

  /** Resolve a state name for display, searching every loaded country's states. */
  stateName = (uuid?: string) => {
    for (const country of (this.props.countryContext.countries ?? []) as any[]) {
      const state = country.states?.find((s: any) => s.uuid === uuid)
      if (state) return state.name
    }
    return undefined
  }

  // ----- Field groups -----

  /** Adopets target — country selector that filters the state options stored in `state_uuid`. */
  renderAdoptsState = () => {
    const { form, countryContext } = this.props

    const countryOptions = countryContext.countries?.map((c: any) => ({
      label: c.name,
      value: c.uuid,
    }))

    return (
      <>
        <Form.Item
          label="Country"
          name="country_uuid"
          rules={[{ required: true, message: 'Missing a country' }]}
        >
          <Select
            showSearch
            filterOption={DEFAULT_SELECT_FILTER_OPTION}
            loading={countryContext.fetching}
            placeholder="Select a country"
            options={countryOptions}
            // Changing the country invalidates the previously picked state.
            onChange={() => form.setFieldsValue({ state_uuid: undefined })}
          />
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.country_uuid !== cur.country_uuid}>
          {({ getFieldValue }) => {
            const countryUuid: string | undefined = getFieldValue('country_uuid')
            const stateList: any[] = (countryContext.countries
              ?.find((c: any) => c.uuid === countryUuid) as any)?.states ?? []

            const stateOptions = stateList.map(s => ({
              label: s.name,
              value: s.uuid,
            }))

            return (
              <Form.Item
                label="State"
                name="state_uuid"
                rules={[{ required: true, message: 'Missing a state' }]}
              >
                <Select
                  showSearch
                  filterOption={DEFAULT_SELECT_FILTER_OPTION}
                  disabled={!countryUuid}
                  placeholder="Select a state"
                  options={stateOptions}
                />
              </Form.Item>
            )
          }}
        </Form.Item>
      </>
    )
  }

  /** External target (Shelter Buddy country → state pickers, or the default name + ids inputs). */
  renderExternalTarget = () => {
    const { form } = this.props

    return (
      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) =>
          prev.shelter_uuid !== cur.shelter_uuid ||
          prev.partner_key !== cur.partner_key ||
          prev.integration_uuid !== cur.integration_uuid ||
          prev.external_country_id !== cur.external_country_id
        }
      >
        {({ getFieldValue }) => {
          const shelterUuid: string | undefined = getFieldValue("shelter_uuid")
          const partnerKey: PartnerKeyType | undefined = getFieldValue("partner_key")
          const integrationUuid: string | undefined = getFieldValue("integration_uuid")
          const externalCountryId: string | undefined = getFieldValue("external_country_id")

          const canLoadExternal =
            !!shelterUuid && !!integrationUuid && partnerKey === "SHELTER_BUDDY"

          if (canLoadExternal) {
            return (
              <>
                <ShelterBuddyExternalField
                  form={form}
                  integrationUuid={integrationUuid!}
                  loadExternalList={loadCountries}
                  name="external_country_id"
                  nameField={null}
                  label="External Country"
                  placeholder="Select a country"
                  // Changing the external country invalidates the previously picked state.
                  onChange={() => form.setFieldsValue({ external_id: undefined, name: undefined })}
                />
                {externalCountryId ? (
                  <ShelterBuddyExternalField
                    form={form}
                    integrationUuid={integrationUuid!}
                    loadExternalList={this.loadStates}
                    reloadKey={externalCountryId}
                    label="External State"
                    placeholder="Select a state"
                  />
                ) : null}
              </>
            )
          }

          return (
            <ManualExternalInputs
              extra={
                <Form.Item
                  label="External Country ID"
                  name="external_country_id"
                  rules={[{ required: true, message: 'Missing an external country id' }]}
                >
                  <Input />
                </Form.Item>
              }
            />
          )
        }}
      </Form.Item>
    )
  }

  /** Inbound only: read-only display of the incoming external value. */
  renderIncoming = () => (
    <IncomingStep
      form={this.props.form}
      partnerList={this.props.partnerList}
      externalRowLabel="External State"
      extraRows={
        <Descriptions.Item label="External Country ID">
          {this.props.form.getFieldValue('external_country_id') || '—'}
        </Descriptions.Item>
      }
      hiddenFields={['partner_key', 'name', 'external_country_id', 'external_id', 'integration_uuid']}
    />
  )

  // ----- Review -----

  renderReview = () => {
    const { form } = this.props

    const partnerKey = form.getFieldValue('partner_key')
    const shelterUuid = form.getFieldValue('shelter_uuid')
    const stateUuid = form.getFieldValue('state_uuid')
    const name = form.getFieldValue('name')
    const externalId = form.getFieldValue('external_id')

    const ourSide = this.stateName(stateUuid) || '—'
    const externalSide = [name, externalId].filter(Boolean).join(' · ') || '—'
    const mappingSummary = `${ourSide} ↔ ${externalSide}`

    return (
      <>
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Direction">
            {DIRECTION_LABEL.BOTH}
          </Descriptions.Item>
          <Descriptions.Item label="Partner">{this.partnerName(partnerKey) || '—'}</Descriptions.Item>
          <Descriptions.Item label="Mapping">{mappingSummary}</Descriptions.Item>
          <Descriptions.Item label="Applies to">
            {shelterUuid ? (this.shelterName(shelterUuid) || shelterUuid) : <Tag>GLOBAL</Tag>}
          </Descriptions.Item>
        </Descriptions>

        <ActiveField />
      </>
    )
  }

  // ----- Step definitions -----

  steps = (): Step[] => {
    const { form, shelterContext } = this.props

    const reviewStep: Step = {
      key: 'review',
      title: 'Review & Save',
      fields: [],
      content: this.renderReview,
    }

    if (this.state.flow === 'inbound') {
      return [
        {
          key: 'incoming',
          title: 'Incoming',
          fields: [],
          content: this.renderIncoming,
        },
        {
          key: 'our-side',
          title: 'Our side',
          fields: ['country_uuid', 'state_uuid', 'shelter_uuid'],
          content: () => (
            <>
              {this.renderAdoptsState()}
              <ScopeField form={form} shelterContext={shelterContext} />
            </>
          ),
        },
        reviewStep,
      ]
    }

    return [
      {
        key: 'our-side',
        title: 'Our side',
        fields: ['country_uuid', 'state_uuid', 'partner_key', 'shelter_uuid'],
        content: () => (
          <>
            {this.renderAdoptsState()}
            <PartnerField partnerList={this.props.partnerList} />
            <ScopeField form={form} shelterContext={shelterContext} />
          </>
        ),
      },
      {
        key: 'external',
        title: 'External target',
        fields: ['name', 'external_country_id', 'external_id'],
        content: () => (
          <>
            <IntegrationField shelterContext={shelterContext} />
            {this.renderExternalTarget()}
          </>
        ),
      },
      reviewStep,
    ]
  }

  render() {
    return <MappingStepper form={this.props.form} steps={this.steps()} loading={this.props.loading} />
  }
}

export default class MappingStateForm extends Component<MappingStateFormProps> {
  render() {
    return (
      <ShelterConsumer>
        {(shelterContext) => (
          <CountryConsumer>
            {(countryContext) => (
              <MappingStateFormInner
                form={this.props.form}
                partnerList={this.props.partnerList}
                loading={this.props.loading}
                shelterContext={shelterContext}
                countryContext={countryContext}
              />
            )}
          </CountryConsumer>
        )}
      </ShelterConsumer>
    )
  }
}
